// THE PROJECT MENUS — v4.0.0 (design p47–p48; docs/ui-refresh/03-help-menus-dialogs.md
// §11 stage picker, §12 project row menu, §13 project actions menu).
//
// One small popover on the .menu-v4 component, shared by the three menus so
// they look, open, close and move by keyboard the same way. Every label goes
// through escapeHtml; icons are the static nav-icons glyphs.
//
// LAWS kept from views/row-menu.js: a row menu holds at most SIX items, its
// first item opens the record, and the ending ("Put on hold") is last, behind a
// separator, in navy — nothing here destroys a record, so nothing here is red.
// Role-gated items stay visible, greyed, with the holding role named at the right.
import { escapeHtml } from '../ui.js';
import { navIcon } from '../nav-icons.js';

let current = null;   // { el, cleanup }

export function closeProjectMenu() {
  if (!current) return;
  current.cleanup();
  current = null;
}

/**
 * items: [{ label, onClick, hint?, icon?, sep?:bool (separator BEFORE it),
 *           disabled?:bool, current?:bool, dot?:'past'|'now'|'next'|'soft',
 *           role?:string (shown right; greyed when allowed === false), allowed?:bool }]
 * at: { anchor: Element, align: 'left'|'right' } or { x, y } (a context menu at the pointer).
 * opts: { header, footer, width, cls }
 */
export function openProjectMenu(at, items, { header = '', footer = '', width = 250, cls = '' } = {}) {
  closeProjectMenu();
  const prevFocus = document.activeElement;
  const el = document.createElement('div');
  el.className = `menu-v4 pmenu${cls ? ` ${cls}` : ''}`;
  el.setAttribute('role', 'menu');
  el.style.width = `${width}px`;
  el.innerHTML = (header ? `<div class="pmenu-h">${escapeHtml(header)}</div>` : '')
    + items.map((it, i) => {
      const gated = it.disabled || (it.role && it.allowed === false);
      return `${it.sep ? '<hr>' : ''}<button type="button" role="menuitem" data-i="${i}"${gated ? ' aria-disabled="true" tabindex="-1"' : ''}
        class="${[it.current ? 'is-now' : '', gated ? 'is-off' : '', it.ending ? 'is-end' : ''].filter(Boolean).join(' ')}">
        ${it.dot ? `<span class="pmenu-dot ${it.dot}" aria-hidden="true"></span>` : ''}${it.icon ? `<span class="pmenu-ico" aria-hidden="true">${navIcon(it.icon, 16)}</span>` : ''}
        <span class="pmenu-l">${escapeHtml(it.label)}</span>
        ${it.hint ? `<span class="pmenu-hint">${escapeHtml(it.hint)}</span>` : it.role ? `<span class="pmenu-hint">${escapeHtml(it.role)}</span>` : ''}
      </button>`;
    }).join('')
    + (footer ? `<div class="pmenu-f">${escapeHtml(footer)}</div>` : '');
  document.body.appendChild(el);

  // Place it: under the anchor (left- or right-aligned), or at the pointer; flip up near the bottom.
  const mw = el.offsetWidth, mh = el.offsetHeight;
  let left, top;
  if (at && at.anchor) {
    const r = at.anchor.getBoundingClientRect();
    left = at.align === 'right' ? r.right - mw : r.left;
    top = r.bottom + 6;
    if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 6);
  } else {
    left = (at && at.x) || 0; top = (at && at.y) || 0;
    if (top + mh > window.innerHeight - 8) top = Math.max(8, top - mh);
  }
  el.style.left = `${Math.round(Math.max(8, Math.min(left, window.innerWidth - mw - 8)))}px`;
  el.style.top = `${Math.round(top)}px`;

  const live = () => [...el.querySelectorAll('button[role="menuitem"]:not([aria-disabled])')];
  const onClick = (ev) => {
    const b = ev.target.closest('button[data-i]');
    if (!b || b.getAttribute('aria-disabled')) return;
    const it = items[+b.dataset.i];
    closeProjectMenu();
    if (it && typeof it.onClick === 'function') it.onClick();
  };
  const onKey = (ev) => {
    if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); closeProjectMenu(); if (prevFocus && prevFocus.focus) prevFocus.focus(); return; }
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault(); ev.stopPropagation();
      const list = live(); if (!list.length) return;
      const ix = list.indexOf(document.activeElement);
      const next = ev.key === 'ArrowDown' ? (ix + 1) % list.length : (ix <= 0 ? list.length - 1 : ix - 1);
      list[next].focus();
    }
  };
  const onDoc = (ev) => { if (!el.contains(ev.target)) closeProjectMenu(); };
  el.addEventListener('click', onClick);
  document.addEventListener('keydown', onKey, true);
  const t = setTimeout(() => { document.addEventListener('pointerdown', onDoc, true); }, 0);
  window.addEventListener('scroll', closeProjectMenu, { once: true, capture: true });
  current = {
    el,
    cleanup: () => {
      clearTimeout(t);
      el.remove();
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDoc, true);
      window.removeEventListener('scroll', closeProjectMenu, true);
    },
  };
  (el.querySelector('button.is-now:not([aria-disabled])') || live()[0])?.focus();
  return el;
}

// ── §11 The stage picker ─────────────────────────────────────────────────────
// Six stages, sentence case for display (stored title case). Past stages are
// disabled: STAGES ONLY MOVE FORWARD (design law D2). No reason step for a
// forward move. Complete is offered only once a project is Issued, so a
// finished project can still be closed out from here.
export const STAGES = ['Not Started', 'In Progress', 'Review', 'Ready to Issue', 'Part Issued', 'Issued'];
const LABEL = { 'Not Started': 'Not started', 'In Progress': 'In progress', Review: 'Review', 'Ready to Issue': 'Ready to issue', 'Part Issued': 'Part issued', Issued: 'Issued', Complete: 'Complete' };
export const stageText = (s) => LABEL[s] || s || 'Not started';

/** The menu's rows for a project at `status` — pure, so it is tested in node. */
export function stageRows(status) {
  const list = status === 'Issued' || status === 'Complete' ? [...STAGES, 'Complete'] : STAGES;
  const rank = list.indexOf(status || 'Not Started');
  return list.map((s, i) => ({
    value: s, label: LABEL[s],
    state: rank < 0 ? 'next' : i < rank ? 'past' : i === rank ? 'now' : 'next',
  }));
}

export function openStagePicker(anchor, { number, status, onPick }) {
  const items = stageRows(status).map((r) => ({
    label: r.label,
    dot: r.state === 'past' ? 'past' : r.state === 'now' ? 'now' : r.value === 'Part Issued' ? 'soft' : 'next',
    current: r.state === 'now', hint: r.state === 'now' ? 'now' : '',
    disabled: r.state !== 'next',
    onClick: () => onPick(r.value),
  }));
  return openProjectMenu({ anchor, align: 'left' }, items, {
    header: `Move ${number || 'this project'} to`, width: 270, cls: 'pmenu-stage',
    footer: 'Stages only move forward. Every change is logged.',
  });
}

/** Log time hands over to the shell's Log time dialog when there is one;
 *  without a listener that takes it, the Time screen opens instead. */
export function requestLogTime({ projectId, projectNo }, fallback) {
  const ev = new CustomEvent('track:log-time', { detail: { projectId, projectNo }, cancelable: true });
  document.dispatchEvent(ev);
  if (!ev.defaultPrevented && typeof fallback === 'function') fallback();
}
